import { UserIdSchema } from "@core/domains/identity";
import { ulid } from "@core/foundation/id";
import {
  createModel,
  createModelId,
  createModelSchemas,
} from "@core/foundation/model";
import { z } from "@core/zod";

import { OAuthScopesSchema } from "@platform/core/models";

import { OAuthClientIdSchema } from "./oauth-client.ts";
import { ID_PREFIX } from "./shared/id-prefix.ts";

const { generateId: generateOAuthRequestId, idSchema: OAuthRequestIdSchema } =
  createModelId({ digestFn: ulid, idPrefix: ID_PREFIX.OAUTH_REQUEST });

export { OAuthRequestIdSchema };

export type OAuthRequestId = z.infer<typeof OAuthRequestIdSchema>;

export const OAuthRequest = createModel({
  create: (item, schema) => {
    return { ...schema.decode(item), id: generateOAuthRequestId() };
  },
  schemas: createModelSchemas({
    defaults: { data: { status: "pending" } },
    model: z.object(
      {
        clientId: OAuthClientIdSchema,
        codeChallenge: z.string(),
        data: z.discriminatedUnion("status", [
          z.object(
            { status: z.literal("pending") },
            { status: "The request's status, awaiting the user's consent." },
          ),
          z.object(
            {
              codeDigest: z.string(),
              principalId: UserIdSchema,
              status: z.literal("approved"),
            },
            {
              codeDigest:
                "The digest of the authorization key the approval issued.",
              principalId: "The id of the user who approved the request.",
              status: "The request's status, approved by the user.",
            },
          ),
          z.object(
            { principalId: UserIdSchema, status: z.literal("spent") },
            {
              principalId: "The id of the user who approved the request.",
              status: "The request's status, its code exchanged for a token.",
            },
          ),
          z.object(
            { status: z.literal("denied") },
            { status: "The request's status, denied by the user." },
          ),
        ]),
        id: OAuthRequestIdSchema,
        redirectUri: z.url(),
        resource: z.url(),
        scopes: OAuthScopesSchema,
        state: z.string().nullable(),
      },
      {
        clientId: "The id of the client requesting the authorization.",
        codeChallenge: "The client's PKCE code challenge.",
        data: "The request's progress, discriminated on its status.",
        id: "The unique identifier for the request.",
        redirectUri: "The URI the browser returns to with the result.",
        resource: "The URL of the resource server the token is requested for.",
        scopes: "The scopes the client requests.",
        state: "The client's opaque state, returned with the result.",
      },
    ),
    modelCreate: ({ readSchema }) => readSchema.omit({ id: true }),
    modelUpdate: ({ schema }) => schema.pick({ data: true }).partial(),
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface OAuthRequest extends z.infer<typeof OAuthRequest.schema> {}
